import { tryJsonParse } from "$root/utils";
import {
	type RESET,
	type RwAtom,
	type WritableAtom,
	atomWithReducer,
	readAtom,
	useAtom,
	writeAtom,
} from "@mdotm/shadcn/legacy/reactivity";
import { type SetterOrUpdaterParam, instanceEqualsOrFastDeepEqual, maybeCall } from "@mdotm/shadcn/legacy/utils";
import { identity } from "@mdotm/utils/functions";

type SetStateActionWithReset<Value> = Value | typeof RESET | ((prev: Value) => Value | typeof RESET);
type SetAtom<Args extends unknown[], Result> = <A extends Args>(...args: A) => Result;

export type JotaiState<T> = {
	state: Awaited<T>;
	setState: SetAtom<[SetStateActionWithReset<T>], void>;
	reset: () => void;
};

export function useJotaiState<T>(
	atom: WritableAtom<T, [SetStateActionWithReset<T>], void>,
	defaultValue: T,
): JotaiState<T> {
	const [state, setState] = useAtom(atom);
	return {
		state,
		setState,
		reset: () => setState(defaultValue),
	};
}
export function atomWithStringStorage(
	storageArea: Storage,
	storageKey: string,
	fallback?: string,
): RwAtom<string | null> {
	const atom = atomWithReducer(
		storageArea.getItem(storageKey) ?? fallback ?? null,
		(value, next: SetterOrUpdaterParam<string | null>) => {
			const nextVal = maybeCall(next, value);
			if (value === nextVal) {
				return value;
			}
			if (nextVal == null) {
				storageArea.removeItem(storageKey);
			} else {
				storageArea.setItem(storageKey, nextVal);
			}
			return nextVal;
		},
	);

	window.addEventListener("storage", (e) => {
		if (e.storageArea === storageArea && e.key === storageKey) {
			writeAtom(atom, e.newValue);
		}
	});

	return atom;
}

export function atomWithJsonStorage<T>(
	storageArea: Storage,
	storageKey: string,
	fallback: T,
	opts?: {
		toPrimitives?: (v: NonNullable<T>) => object | null;
		/** Can throw to indicate that T cannot be reconstructed by the parsed JSON found in the storage area */
		fromPrimitives?: (v: object | null) => T;
	},
): RwAtom<T> {
	const atom = atomWithReducer(
		tryJsonParse(storageArea.getItem(storageKey) ?? "", fallback, opts?.fromPrimitives),
		(value, next: SetterOrUpdaterParam<T>) => {
			const nextVal = maybeCall(next, value);
			const nextValSerialized = nextVal == null ? null : (opts?.toPrimitives ?? identity)(nextVal);
			if (
				(value == null && nextVal == null) ||
				(value != null &&
					nextVal != null &&
					instanceEqualsOrFastDeepEqual((opts?.toPrimitives ?? identity)(value), nextValSerialized))
			) {
				return value;
			}
			if (nextVal == null) {
				storageArea.removeItem(storageKey);
			} else {
				storageArea.setItem(storageKey, JSON.stringify(nextValSerialized));
			}
			return nextVal;
		},
	);

	window.addEventListener("storage", (e) => {
		if (e.storageArea === storageArea && e.key === storageKey) {
			try {
				const deserialized = tryJsonParse(storageArea.getItem(storageKey) ?? "", fallback, opts?.fromPrimitives);
				if (instanceEqualsOrFastDeepEqual(readAtom(atom), deserialized)) {
					// ignore
				} else {
					writeAtom(atom, deserialized);
				}
			} catch (err) {
				console.warn(err);
			}
		}
	});

	return atom;
}
